import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnSizingState,
  type RowData,
} from '@tanstack/react-table';
import { Fragment, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

/**
 * 공용 데이터 테이블 — TanStack Table 기반, 디자인은 ui.tsx 와 동일한 Toss 스타일.
 *
 * 컬럼 리사이즈 규칙:
 * - 첫 렌더는 브라우저 auto 레이아웃 → 마운트 직후 실제 폭을 측정해 컬럼 상태로 고정
 * - 중간 컬럼을 조절하면 마지막 컬럼이 남는 공간을 흡수 (표는 컨테이너 폭 유지,
 *   마지막 컬럼이 minSize 에 닿으면 그때부터 가로 스크롤)
 * - 마지막 컬럼 핸들은 자기 자신을 조절 (표가 카드보다 좁아질 수 있음)
 * - 핸들 더블클릭 → 전체 초기화 후 재측정
 */

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** 헤더·셀 공통 정렬 */
    align?: 'left' | 'center' | 'right';
    /** td 추가 클래스 (whitespace-nowrap, tabular-nums 등) */
    tdClass?: string;
  }
}

const MIN_COL_WIDTH = 56;

export function DataTable<T>({
  data,
  columns,
  getRowId,
  onRowClick,
  renderExpanded,
}: {
  data: T[];
  columns: ColumnDef<T>[];
  getRowId?: (row: T, index: number) => string;
  onRowClick?: (row: T) => void;
  /** 행 아래 펼침 콘텐츠 — null/undefined 가 아니면 colSpan 행으로 렌더 */
  renderExpanded?: (row: T) => ReactNode;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const [sizing, setSizing] = useState<ColumnSizingState>({});
  const measured = Object.keys(sizing).length > 0;

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...(getRowId ? { getRowId } : {}),
    columnResizeMode: 'onChange',
    defaultColumn: { minSize: MIN_COL_WIDTH },
    state: { columnSizing: sizing },
    onColumnSizingChange: (updater) => {
      setSizing((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        return fitLastColumn(next, prev);
      });
    },
  });

  /** 중간 컬럼 조절 시 마지막 컬럼이 남는 공간을 흡수해 표를 컨테이너 폭에 맞춘다. */
  function fitLastColumn(next: ColumnSizingState, prev: ColumnSizingState): ColumnSizingState {
    const cols = table.getAllLeafColumns();
    const last = cols[cols.length - 1];
    const container = wrapRef.current?.clientWidth;
    if (!last || !container) return next;
    // 마지막 컬럼 자체를 드래그한 경우는 그대로 둔다 (표가 좁아지는 건 명시적 의도)
    if (next[last.id] !== prev[last.id]) return next;
    const othersSum = cols.slice(0, -1).reduce((sum, c) => sum + (next[c.id] ?? c.getSize()), 0);
    const lastMin = last.columnDef.minSize ?? MIN_COL_WIDTH;
    return { ...next, [last.id]: Math.max(lastMin, container - othersSum) };
  }

  // 마운트(또는 초기화) 직후 auto 레이아웃이 계산한 실제 컬럼 폭을 상태로 고정.
  // 주의: 컨테이너가 아직 0폭이거나 레이아웃 전이면 측정을 통째로 건너뛴다 —
  // 일부 컬럼만 고정되면 나머지가 TanStack 기본폭(150px)으로 굳어 표가 짧게 끊긴다.
  // 측정 실패 시 데이터 변화 시점에 재시도. 그 사이엔 auto 레이아웃(w-full)이라 표시는 정상.
  const measuredRowCount = useRef(0);
  useLayoutEffect(() => {
    if (measured) return;
    const ths = theadRef.current?.rows[0]?.cells;
    const container = wrapRef.current?.clientWidth;
    if (!ths || !container) return;
    const next: ColumnSizingState = {};
    const autoIds: string[] = [];
    const headers = table.getFlatHeaders();
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]!;
      // 컬럼 def 에 size 를 명시한 경우 자동 측정 대신 그 값을 존중
      const explicit = header.column.columnDef.size;
      const width = explicit ?? ths[i]?.getBoundingClientRect().width;
      if (!width) return; // 레이아웃 전 — 전부-아니면-전무, 다음 렌더에 재시도
      next[header.column.id] = width;
      if (explicit === undefined) autoIds.push(header.column.id);
    }
    const sum = Object.values(next).reduce((a, b) => a + b, 0);
    const lastCol = table.getAllLeafColumns().at(-1);
    if (lastCol && sum < container) {
      // 측정 합이 컨테이너보다 작으면 마지막 컬럼이 남는 폭을 흡수해 표가 카드를 채운다
      next[lastCol.id] = (next[lastCol.id] ?? 0) + container - sum;
    } else if (sum > container && autoIds.length > 0) {
      // 넘치면 크기를 정하지 않은 컬럼들을 비율대로 줄여 카드 안에 맞춘다 — 끝 컬럼이 잘려 보이지 않게.
      // 그래도 최소 폭 때문에 넘치면 그때만 가로 스크롤
      const autoSum = autoIds.reduce((a, id) => a + (next[id] ?? 0), 0);
      const room = container - (sum - autoSum);
      const ratio = Math.max(0, room) / autoSum;
      for (const id of autoIds)
        next[id] = Math.max(MIN_COL_WIDTH, Math.floor((next[id] ?? 0) * ratio));
      const fitted = Object.values(next).reduce((a, b) => a + b, 0);
      if (lastCol && fitted < container)
        next[lastCol.id] = (next[lastCol.id] ?? 0) + container - fitted;
    }
    measuredRowCount.current = data.length;
    setSizing(next);
  }, [measured, table, data.length]);

  // 빈 목록 상태에서 측정했다가 데이터가 도착하면 초기화 후 재측정 (내용 기반 폭으로)
  useLayoutEffect(() => {
    if (measured && measuredRowCount.current === 0 && data.length > 0) setSizing({});
  }, [measured, data.length]);

  const isResizing = table.getState().columnSizingInfo.isResizingColumn;

  return (
    <div
      ref={wrapRef}
      className="overflow-x-auto rounded-lg border border-border bg-card shadow-soft"
    >
      <table
        className={`text-sm ${isResizing ? 'cursor-col-resize select-none' : ''}`}
        style={
          measured ? { tableLayout: 'fixed', width: table.getCenterTotalSize() } : { width: '100%' }
        }
      >
        <thead ref={theadRef}>
          <tr className="border-b border-border bg-muted/60">
            {table.getFlatHeaders().map((header) => {
              const align = header.column.columnDef.meta?.align;
              const resizing = header.column.getIsResizing();
              return (
                <th
                  key={header.id}
                  // 측정 전에도 크기를 정한 컬럼은 그 폭으로 — 측정값과 실제 배치가 어긋나 표가 넘치지 않게
                  style={
                    measured
                      ? { width: header.getSize() }
                      : header.column.columnDef.size
                        ? { width: header.column.columnDef.size }
                        : undefined
                  }
                  className={`relative overflow-hidden whitespace-nowrap px-4 py-3 text-xs font-semibold text-muted-foreground ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'}`}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  <span
                    role="separator"
                    aria-orientation="vertical"
                    title="드래그로 너비 조절 · 더블클릭으로 초기화"
                    onMouseDown={header.getResizeHandler()}
                    onTouchStart={header.getResizeHandler()}
                    onDoubleClick={() => setSizing({})}
                    onClick={(e) => e.stopPropagation()}
                    className={`absolute inset-y-0 right-0 z-10 w-1.5 cursor-col-resize touch-none select-none after:absolute after:right-0.5 after:w-px after:bg-border hover:after:inset-y-0 hover:after:w-0.5 hover:after:bg-brand ${resizing ? 'after:inset-y-0 after:w-0.5 after:bg-brand' : 'after:inset-y-2'}`}
                  />
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => {
            const expanded = renderExpanded?.(row.original);
            return (
              <Fragment key={row.id}>
                <tr
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  className={`border-b border-border/60 transition last:border-0 hover:bg-muted/40 ${onRowClick ? 'cursor-pointer' : ''}`}
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta;
                    return (
                      <td
                        key={cell.id}
                        className={`overflow-hidden text-ellipsis px-4 py-3 align-middle ${meta?.align === 'right' ? 'text-right' : meta?.align === 'center' ? 'text-center' : ''} ${meta?.tdClass ?? ''}`}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
                {expanded != null && (
                  <tr className="border-b border-border/60 bg-muted/30 last:border-0">
                    <td colSpan={row.getVisibleCells().length} className="px-6 py-5">
                      {expanded}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
